import React, { useState, useEffect } from 'react'
import Input from 'aldoo-ra/Input'
import Typography from 'aldoo-ra/Typography'
import Button from 'aldoo-ra/Button'
import { XMarkIcon } from '@heroicons/react/24/outline'

const SettingsPlugin = ({ editor, onClose }) => {
  const [settings, setSettings] = useState({
    slug: '',
    name: '',
    id: '',
    description: '',
  })

  useEffect(() => {
    // Load existing settings from content
    if (editor.content) {
      // Extract slug from link if it exists
      const link = editor.content.link?.[0]
      const linkContent = link?.content || link
      const existingSlug = linkContent?.url || editor.content.slug || editor.content.id || ''

      setSettings({
        slug: existingSlug,
        name: editor.content.name || '',
        id: editor.content.id || '',
        description: editor.content.description || '',
      })
    }
  }, [editor.content])

  const handleChange = (field, value) => {
    const newSettings = { ...settings, [field]: value }
    setSettings(newSettings)

    // Update page content directly
    if (field === 'slug') {
      // Update slug and link label together
      const linkLabel = settings.name || value
      editor.updateContent({
        slug: value,
        link: [{
          label: linkLabel,
          content: {
            url: value,
            type: 'page'
          }
        }]
      })
    } else if (field === 'name') {
      // When name changes, also update the link label
      const currentSlug = settings.slug || editor.content?.id || ''
      editor.updateContent({
        name: value,
        link: [{
          label: value,
          content: {
            url: currentSlug,
            type: 'page'
          }
        }]
      })
    } else {
      editor.updateContent({
        [field]: value,
      })
    }
  }

  return (
    <div className="flex flex-col h-full">
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-1.5 rounded hover:bg-admin_neutral_1/50 text-admin_text_secondary hover:text-admin_error transition-colors z-10"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
        <div className="max-w-3xl mx-auto space-y-4">
        <div>
          <Typography variant="admin_label" className="mb-2">
            URL Slug
          </Typography>
          <Input
            variant="admin"
            value={settings.slug}
            onChange={(e) => handleChange('slug', e.target.value)}
            placeholder="page-url-slug"
          />
          <Typography variant="admin_txt1_dim" className="text-xs mt-1">
            The URL path for this page (e.g., /page-url-slug)
          </Typography>
        </div>

        <div>
          <Typography variant="admin_label" className="mb-2">
            Page Name
          </Typography>
          <Input
            variant="admin"
            value={settings.name}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="Enter page name..."
          />
          <Typography variant="admin_txt1_dim" className="text-xs mt-1">
            Display name for this page
          </Typography>
        </div>

        <div>
          <Typography variant="admin_label" className="mb-2">
            Page ID
          </Typography>
          <Input
            variant="admin"
            value={settings.id}
            onChange={(e) => handleChange('id', e.target.value)}
            placeholder="page-id"
            disabled={!!editor.content?.id}
          />
          <Typography variant="admin_txt1_dim" className="text-xs mt-1">
            {editor.content?.id ? 'ID cannot be changed after creation' : 'Unique identifier for this page'}
          </Typography>
        </div>

        <div>
          <Typography variant="admin_label" className="mb-2">
            Description
          </Typography>
          <textarea
            className="w-full px-3 py-2 bg-admin_canvas border border-admin_bordered rounded-lg text-admin_text resize-none"
            rows={3}
            value={settings.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Enter page description..."
          />
          <Typography variant="admin_txt1_dim" className="text-xs mt-1">
            Internal description for reference
          </Typography>
        </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPlugin
